import React, { useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { SubscriptionProvider } from './contexts/SubscriptionContext';
import LoginPage from './pages/LoginPage';
import SSOCallbackPage from './pages/SSOCallbackPage';
import SSOCompletePage from './pages/SSOCompletePage';
import AuthCallback from './pages/AuthCallback';
import OnboardingPage from './pages/OnboardingPage';
import DashboardPage from './pages/DashboardPage';
import MyTasksPage from './pages/MyTasksPage';
import InsightsPage from './pages/InsightsPage';
import ProjectsPage from './pages/ProjectsPage';
import ClientsPage from './pages/ClientsPage';
import DocumentsPage, { DocumentEditPage } from './pages/DocumentsPage';
import ProjectBoardPage from './pages/ProjectBoardPage';
import TeamPage from './pages/TeamPage';
import SettingsPage from './pages/SettingsPage';
import TaskDetailPage from './pages/TaskDetailPage';
import ProjectSettingsPage from './pages/ProjectSettingsPage';
import BillingPage from './pages/BillingPage';
import InvoicePage from './pages/InvoicePage';
import ContactSupportPage from './pages/ContactSupportPage';
import AccountPage from './pages/AccountPage';
import ServiceDeskQueuePage from './pages/serviceDesk/ServiceDeskQueuePage';
import ServiceDeskSettingsPage from './pages/serviceDesk/ServiceDeskSettingsPage';
import PortalQueuePage from './pages/serviceDesk/PortalQueuePage';
import PortalConfigPage from './pages/serviceDesk/PortalConfigPage';
import FormBuilderPage from './pages/serviceDesk/FormBuilderPage';
import TicketDetailPage from './pages/serviceDesk/TicketDetailPage';
import CustomersPage from './pages/serviceDesk/CustomersPage';
import PortalLayout from './pages/portal/PortalLayout';
import PortalHomePage from './pages/portal/PortalHomePage';
import PortalGroupPage from './pages/portal/PortalGroupPage';
import PortalRequestFormPage from './pages/portal/PortalRequestFormPage';
import PortalLoginPage from './pages/portal/PortalLoginPage';
import PortalMyRequestsPage from './pages/portal/PortalMyRequestsPage';
import PortalTicketDetailPage from './pages/portal/PortalTicketDetailPage';
import PortalProfilePage from './pages/portal/PortalProfilePage';
import CustomerSetPasswordPage from './pages/portal/CustomerSetPasswordPage';
import { ThemeProvider, useTheme } from './contexts/ThemeContext';
import HelpCentreLayout from './pages/helpCentre/HelpCentreLayout';
import HelpCentreHomePage from './pages/helpCentre/HelpCentreHomePage';
import HelpCentreLoginPage from './pages/helpCentre/HelpCentreLoginPage';
import HelpCentreRequestsPage from './pages/helpCentre/HelpCentreRequestsPage';
import HelpCentresPage from './pages/serviceDesk/HelpCentresPage';
import HelpCentreConfigPage from './pages/serviceDesk/HelpCentreConfigPage';
import CmdbPage from './pages/serviceDesk/CmdbPage';
import KnowledgePage from './pages/serviceDesk/KnowledgePage';
import SetupAccountPage from './pages/SetupAccountPage';
import VerifyEmailPage from './pages/VerifyEmailPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import TermsOfServicePage from './pages/TermsOfServicePage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import { Sidebar } from './components/Sidebar';
import CreateProjectDialog from './components/CreateProjectDialog';
import ImpersonationBanner from './components/ImpersonationBanner';
import ExpiredBanner from './components/ExpiredBanner';
import AppFooter from './components/AppFooter';
import { SuperAdminProvider } from './contexts/SuperAdminContext';
import { ConfirmProvider } from './components/ConfirmDialog';
import { Toaster } from './components/ui/sonner';
import SALogin from './pages/superadmin/SALogin';
import SADashboard from './pages/superadmin/SADashboard';
import SAOrganizations from './pages/superadmin/SAOrganizations';
import SAOrgDetail from './pages/superadmin/SAOrgDetail';
import SASupportTickets from './pages/superadmin/SASupportTickets';
import SAFeatureRegistry from './pages/superadmin/SAFeatureRegistry';
import './App.css';

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto" />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function OnboardingGuard({ children }) {
  const { needsOnboarding } = useAuth();
  if (needsOnboarding) return <Navigate to="/onboarding" replace />;
  return children;
}

function AppLayout() {
  return (
    <ThemeProvider>
      <AppShell />
    </ThemeProvider>
  );
}

function AppShell() {
  const [showCreateProject, setShowCreateProject] = useState(false);
  const { appTheme } = useTheme();
  const navigate = useNavigate();

  return (
    <div className="app-shell flex h-screen bg-white overflow-hidden" data-theme={appTheme}>
      <Sidebar onCreateProject={() => setShowCreateProject(true)} />
      <main className="flex-1 flex flex-col overflow-hidden min-h-0" data-testid="app-main">
        <ImpersonationBanner />
        <ExpiredBanner />
        <Routes>
          <Route path="/dashboard" element={<DashboardPage />} />
          <Route path="/my-tasks" element={<MyTasksPage />} />
          <Route path="/insights" element={<InsightsPage />} />
          <Route path="/projects" element={<ProjectsPage />} />
          <Route path="/projects/:projectId" element={<ProjectBoardPage />} />
          <Route path="/projects/:projectId/settings" element={<ProjectSettingsPage />} />
          <Route path="/clients" element={<ClientsPage />} />
          <Route path="/documents" element={<DocumentsPage />} />
          <Route path="/documents/:docId" element={<DocumentEditPage />} />
          <Route path="/tasks/:taskId" element={<TaskDetailPage />} />
          {/* Jira-style permalink. /browse/WORK01-4 renders the SAME
              TaskDetailPage in-place — the URL stays as the human-readable
              key (no redirect to opaque task_id) so it stays pasteable. */}
          <Route path="/browse/:taskKey" element={<TaskDetailPage />} />
          <Route path="/team" element={<TeamPage />} />
          <Route path="/billing" element={<BillingPage />} />
          <Route path="/invoice/:transactionId" element={<InvoicePage />} />
          <Route path="/settings" element={<SettingsPage />} />
          <Route path="/account" element={<AccountPage />} />
          <Route path="/contact-support" element={<ContactSupportPage />} />
          {/* Service Desk (Phase 1) — agent-side. Routes are visible only
              when the org has the service_desk add-on enabled; pages
              redirect to an upsell card otherwise. */}
          <Route path="/service-desk" element={<ServiceDeskQueuePage />} />
          <Route path="/service-desk/settings" element={<ServiceDeskSettingsPage />} />
          <Route path="/service-desk/settings/portal/:portalId" element={<PortalConfigPage />} />
          <Route path="/service-desk/settings/portal/:portalId/form/:rtId" element={<FormBuilderPage />} />
          <Route path="/service-desk/portal/:portalId" element={<PortalQueuePage />} />
          <Route path="/service-desk/portal/:portalId/ticket/:ticketId" element={<TicketDetailPage />} />
          <Route path="/service-desk/customers" element={<CustomersPage />} />
          <Route path="/service-desk/help-centres" element={<HelpCentresPage />} />
          <Route path="/service-desk/help-centres/:hcId" element={<HelpCentreConfigPage />} />
          <Route path="/service-desk/cmdb" element={<CmdbPage />} />
          <Route path="/service-desk/knowledge" element={<KnowledgePage />} />
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </main>
      <CreateProjectDialog open={showCreateProject} onClose={() => setShowCreateProject(false)} onCreated={() => { setShowCreateProject(false); window.dispatchEvent(new Event('project-created')); }} onImportJira={() => navigate('/projects?import=jira')} />
    </div>
  );
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes('session_id=')) return <AuthCallback />;

  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/sso-callback" element={<SSOCallbackPage />} />
      <Route path="/sso-complete" element={<SSOCompletePage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/setup-account/:token" element={<SetupAccountPage />} />
      <Route path="/verify-email/:token" element={<VerifyEmailPage />} />
      <Route path="/terms" element={<TermsOfServicePage />} />
      <Route path="/privacy" element={<PrivacyPolicyPage />} />
      <Route path="/onboarding" element={<ProtectedRoute><OnboardingPage /></ProtectedRoute>} />
      {/* Public-facing customer help portal (Phase 2). No app auth — has
          its own customer-token session. Path-based fallback works in dev;
          production uses subdomain routing via the Host header. */}
      <Route path="/help/:slug" element={<PortalLayout />}>
        <Route index element={<PortalHomePage />} />
        <Route path="group/:groupId" element={<PortalGroupPage />} />
        <Route path="login" element={<PortalLoginPage />} />
        <Route path="requests" element={<PortalMyRequestsPage />} />
        <Route path="requests/:ticketKey" element={<PortalTicketDetailPage />} />
        <Route path="request/:rtId" element={<PortalRequestFormPage />} />
        <Route path="profile" element={<PortalProfilePage />} />
      </Route>
      <Route path="/customer/set-password" element={<CustomerSetPasswordPage />} />
      <Route path="/helpcentre/:slug" element={<HelpCentreLayout />}>
        <Route index element={<HelpCentreHomePage />} />
        <Route path="login" element={<HelpCentreLoginPage />} />
        <Route path="requests" element={<HelpCentreRequestsPage />} />
      </Route>
      <Route path="/superadmin/*" element={<SuperAdminProvider><SuperAdminRoutes /></SuperAdminProvider>} />
      <Route path="/*" element={<ProtectedRoute><OnboardingGuard><SubscriptionProvider><AppLayout /></SubscriptionProvider></OnboardingGuard></ProtectedRoute>} />
    </Routes>
  );
}

function SuperAdminRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<SALogin />} />
      <Route path="/dashboard" element={<SADashboard />} />
      <Route path="/organizations" element={<SAOrganizations />} />
      <Route path="/organizations/:orgId" element={<SAOrgDetail />} />
      <Route path="/support" element={<SASupportTickets />} />
      <Route path="/feature-registry" element={<SAFeatureRegistry />} />
      <Route path="*" element={<Navigate to="/superadmin/dashboard" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <ConfirmProvider>
          <AppRouter />
          {/* Global toast portal — required so toast.error/.success/.info actually
              render. Without this, every toast call across the app is a no-op. */}
          <Toaster richColors position="top-right" closeButton />
        </ConfirmProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;

